import { Box, Chip, IconButton, Typography } from "@mui/material"
import { MoreVertOutlined } from "@mui-symbols-material/w300"
import { type FC, useState } from "react"

import { useGetUser, useUpdateUser } from "../hooks"

import UserDropdownMenu from "./UserDropdownMenu"

import { CancelTemplate, ModalContainer } from "@/components/common/Modals"
import type { User } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useCurrentUserStore } from "@/stores/currentUserStore"
import { useNotificationsStore } from "@/stores/useNotificationsStore"
import { formatDate } from "@/utils/date"

const UserDetailsHeader: FC<{ user: User }> = ({ user }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [isDeactivateConfirmationOpened, setIsDeactivateConfirmationOpened] = useState(false)
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const { update, fetching } = useUpdateUser()
  const { reexecuteQuery } = useGetUser(user.userId)

  const currentUser = useCurrentUserStore((state) => state.currentUser)

  const isCurrentUser = currentUser?.userId === user.userId

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleCloseMenu = () => {
    setAnchorEl(null)
  }

  const { name, email, isActive, lastLoginAt, loginId, phoneNumber, createdAt } = user

  const handleActivate = async () => {
    handleCloseMenu()
    const result = await update({
      input: {
        userId: user.userId,
        isActive: true,
      },
    })

    if (result.error) {
      notify({
        type: NotificationType.Error,
        message: "Failed to activate user",
      })

      return
    }

    notify({
      type: NotificationType.Success,
      message: "User Reactivated!",
    })
    reexecuteQuery()
  }

  const handleDeactivate = async () => {
    const result = await update({
      input: {
        userId: user.userId,
        isActive: false,
      },
    })

    if (result.error) {
      notify({
        type: NotificationType.Error,
        message: "Failed to deactivate user",
      })

      return
    }

    reexecuteQuery()

    notify({
      type: NotificationType.Error,
      silent: false,
      message: "User Deactivated!",
    })
  }

  return (
    <Box className='flex justify-between rounded-md border border-solid border-gray-300 p-6'>
      <Box>
        <Box className='flex items-center gap-3'>
          <Typography variant='h5' className='text-gray-800'>
            {name}
          </Typography>
          <Chip label={isActive ? "Active" : "Inactive"} variant='outlined' color={isActive ? "success" : "error"} />
        </Box>

        <Box className='mt-6 flex flex-wrap items-center gap-5'>
          {[
            ["Login", loginId],
            ["Email", email],
            ["Phone", phoneNumber],
          ].map(([label, value]) => (
            <Box key={label} className='flex items-center gap-2'>
              <Typography variant='body2' fontWeight={500} className='text-gray-700'>
                {label}:
              </Typography>
              <Typography variant='body2' className='text-gray-700'>
                {value ?? "-"}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Box className='flex items-center justify-between gap-4'>
        <Box className='flex items-center justify-between gap-4'>
          {[
            ["Created", createdAt],
            ["Last Logged", lastLoginAt],
          ].map(([label, value]) => (
            <Box key={label} className='flex flex-wrap items-center gap-1'>
              <Typography variant='body2' className='text-gray-700'>
                {label}
              </Typography>

              <Chip label={formatDate(value)} className='rounded-sm' />
            </Box>
          ))}
        </Box>

        {!isCurrentUser && (
          <Box className='flex flex-col justify-between'>
            <IconButton color='primary' aria-label='company actions' onClick={handleOpenMenu}>
              <MoreVertOutlined />
            </IconButton>
            <UserDropdownMenu
              anchorEl={anchorEl}
              onClose={handleCloseMenu}
              isActive={!!isActive}
              onDeactivate={() => {
                setIsDeactivateConfirmationOpened(true)
                handleCloseMenu()
              }}
              onActivate={handleActivate}
              disabled={fetching}
            />
          </Box>
        )}
      </Box>

      <ModalContainer isOpen={isDeactivateConfirmationOpened} onClose={() => setIsDeactivateConfirmationOpened(false)}>
        <CancelTemplate
          title='Are you sure you want to deactivate this user?'
          message='By deactivating this user, they will lose the ability to log in. Confirm Deactivation or Go back.'
          confirmLabel='Yes, Deactivate'
          onClose={() => setIsDeactivateConfirmationOpened(false)}
          onConfirm={handleDeactivate}
        />
      </ModalContainer>
    </Box>
  )
}

export default UserDetailsHeader
